import { useState, useEffect } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import clsx from "clsx";

import { View, Text, Button } from "../components/base";
import { LeftNavbar, SidebarSection } from "../components/LeftNavbar";
import { RequireAuth } from "../components/auth/RequireAuth";
import { Icon } from "../components/base/Icon";
import { useEffectiveOrganization } from "../hooks/useEffectiveOrganization";
import { OrganizationContextIndicator } from "../components/OrganizationContextIndicator";
import { useAuth } from "../hooks/useAuth";
import { CommandPaletteProvider, CommandPalette } from "../components/CommandPalette";

export const StandardLayout = () => {
  const { isAdmin } = useAuth();
  const { organization } = useEffectiveOrganization();
  const isVoiceRun = typeof window !== "undefined" && window.location.hostname.includes("voicerun");
  const supportEmail = isVoiceRun ? "support@voicerun.com" : "support@primvoices.com";
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem("sidebarCollapsed");
    return saved !== null ? JSON.parse(saved) : false;
  });

  // Save to localStorage whenever collapsed state changes
  useEffect(() => {
    localStorage.setItem("sidebarCollapsed", JSON.stringify(isSidebarCollapsed));
  }, [isSidebarCollapsed]);

  // Auto-collapse sidebar when debugger is open
  useEffect(() => {
    const handleDebuggerCollapse = () => {
      const isOnFunctionPage = location.pathname.includes("/function");
      if (!isOnFunctionPage) return;

      const isDebuggerOpen = document.querySelector('[aria-label*="Collapse debug sidebar"]') !== null;

      if (isDebuggerOpen && !isSidebarCollapsed) {
        // Store original state before auto-collapsing
        if (!localStorage.getItem("autoCollapsedForDebugger")) {
          localStorage.setItem("autoCollapsedForDebugger", "true");
          localStorage.setItem("originalSidebarState", JSON.stringify(isSidebarCollapsed));
        }
        setIsSidebarCollapsed(true);
      } else if (!isDebuggerOpen && localStorage.getItem("autoCollapsedForDebugger")) {
        // Restore original state when debugger closes
        const originalState = localStorage.getItem("originalSidebarState");
        if (originalState !== null) {
          setIsSidebarCollapsed(JSON.parse(originalState));
          localStorage.removeItem("autoCollapsedForDebugger");
          localStorage.removeItem("originalSidebarState");
        }
      }
    };

    // Check on mount
    handleDebuggerCollapse();

    // Use MutationObserver to detect when debugger opens/closes
    const observer = new MutationObserver(() => {
      // Add a small delay to ensure DOM changes are complete
      setTimeout(handleDebuggerCollapse, 100);
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["aria-label"],
    });

    return () => {
      observer.disconnect();
    };
  }, [location.pathname, isSidebarCollapsed]);

  const sidebarSections: SidebarSection[] = [
    {
      title: "",
      links: [
        { label: "Agents", to: "/agents" },
        { label: "Evaluators", to: "/evaluators" },
        ...(isAdmin ? [{ label: "Simulations", to: "/agents/simulations" }] : []),
        { label: "Recipes", to: "/agents/recipes" },
        { label: "Organization", to: "/organization" },
        { label: "Voice Library", to: "/voice-library" },
        { label: "Resources", to: "/resources" },
        ...(isAdmin ? [{ label: "Admin", to: "/admin" }] : []),
      ],
    },
  ];

  const onClickSignout = () => {
    navigate("/signout");
  };

  return (
    <CommandPaletteProvider>
      <RequireAuth>
        <View className="relative flex w-full h-screen bg-gray-50 dark:bg-black">
          {organization?.agentsEnabled && (
            <LeftNavbar
              sections={sidebarSections}
              isOpen={isMobileMenuOpen}
              onClose={() => setIsMobileMenuOpen(false)}
              isCollapsed={isSidebarCollapsed}
              onToggleCollapse={() => {
                const isOnFunctionPage = location.pathname.includes("/function");
                const isDebuggerOpen = document.querySelector('[aria-label*="Collapse debug sidebar"]') !== null;

                // If trying to expand sidebar while debugger is open on function page, close debugger instead
                if (isOnFunctionPage && isDebuggerOpen && isSidebarCollapsed) {
                  // Find and click the debugger close button
                  const debuggerCloseButton = document.querySelector('[aria-label*="Collapse debug sidebar"]');
                  if (debuggerCloseButton) {
                    (debuggerCloseButton as HTMLElement).click();
                  }
                } else {
                  // Normal toggle behavior
                  setIsSidebarCollapsed(!isSidebarCollapsed);
                }
              }}
            />
          )}
          <View className={clsx("flex flex-col flex-1 min-w-0", organization?.agentsEnabled && (isSidebarCollapsed ? "lg:ml-[60px]" : "lg:ml-[220px]"))}>
            {/* Mobile hamburger button */}
            {organization?.agentsEnabled && (
              <Button
                type="button"
                variant="ghost"
                onClick={() => setIsMobileMenuOpen(true)}
                className="hamburger-button lg:hidden absolute top-4 left-4 z-40 !h-10 !w-10 rounded-md bg-white dark:bg-[#0a0a0a] border border-gray-200 dark:border-[#1a1a1a] hover:bg-gray-100 dark:hover:bg-[#0a0a0a]/80 transition-colors flex items-center justify-center shadow-md"
              >
                <Icon
                  name="menu"
                  size={20}
                  className="text-gray-600 dark:text-[#666]"
                />
              </Button>
            )}
            <OrganizationContextIndicator />
            <View className="flex-1 h-full bg-gray-50 dark:bg-black min-w-0">
              {organization?.agentsEnabled ? (
                <Outlet />
              ) : (
                <View className="flex flex-col items-center justify-center w-full h-full px-4">
                  <View className="flex flex-col items-center gap-4 bg-gray-50 dark:bg-gray-800 rounded-xl p-8 shadow-md max-w-md">
                    <Icon
                      name="sparkles"
                      className="text-brand text-4xl mb-2"
                    />
                    <Text className="text-2xl font-semibold text-gray-900 dark:text-white mb-2 text-center">Thank you for your interest!</Text>
                    <Text className="text-base text-gray-700 dark:text-gray-200 text-center">
                      The platform is currently in{" "}
                      <Text
                        as="span"
                        className="font-semibold text-brand"
                      >
                        beta
                      </Text>
                      . Our team will be in touch soon to help you get started.
                    </Text>
                    <Text className="text-sm text-gray-500 dark:text-gray-400 text-center">We appreciate your patience as we roll out the platform.</Text>
                    <Text className="text-sm text-gray-500 dark:text-gray-400 text-center">
                      For further support, please contact{" "}
                      <a
                        href={`mailto:${supportEmail}`}
                        className="font-semibold text-brand dark:text-white hover:underline"
                      >
                        {supportEmail}
                      </a>
                    </Text>
                    <Button
                      variant="secondary"
                      onClick={onClickSignout}
                    >
                      <Icon
                        name="logout"
                        className="text-lg mr-2"
                      />
                      <Text>Sign out</Text>
                    </Button>
                  </View>
                </View>
              )}
            </View>
          </View>
        </View>
        <CommandPalette />
      </RequireAuth>
    </CommandPaletteProvider>
  );
};
